/**
 * Data model + pure helpers for the Create Monthly Challenge wizard's
 * "Challenges" step (FE-2252).
 *
 * The monthly flow reuses the Game Pass reward vocabulary (reward types, the
 * integer-only sanitiser) from `createSeasonData` so the two wizards can never
 * drift, but its challenge shape is deliberately simpler than a season's: a
 * monthly challenge is a single task type with a list of escalating tiers
 * (each tier = a threshold to reach + one reward), edited inline — there is no
 * per-task modal, FREE/PREMIUM path, or sequential toggle here.
 */
import {
  DESCRIPTION_MAX,
  REWARD_TYPE_OPTIONS,
  humanizeParamName,
  sanitizeDecimalInput,
  sanitizeIntegerInput,
  sanitizeParamInput,
  type BetCurrency,
  type RewardType,
} from "../CreateSeason/createSeasonData";

export {
  DESCRIPTION_MAX,
  REWARD_TYPE_OPTIONS,
  humanizeParamName,
  sanitizeDecimalInput,
  sanitizeIntegerInput,
  sanitizeParamInput,
};
export type { BetCurrency, RewardType };

/**
 * Monthly challenge task types. "Follow for follow" shipped in FE-2252;
 * "Rebets" (a wager-threshold challenge rewarding Rebet Cash / Coins) was added
 * in FE-2253 and gained the full sportsbook trigger fields (odds, sports /
 * leagues, bet amount) in FE-2441; "Referral Bonus" (tiered referral thresholds, each step a
 * referral milestone the player must hit to earn that step's reward) in FE-2254;
 * "Sportsbook Picks" (qualifying-pick thresholds, gated by a Pick Type and a
 * Minimum Odds floor) in FE-2255; "Casino Picks" (qualifying casino plays/wins,
 * gated by a Provider, a set of Game Titles and a Play/Win Action) in FE-2256;
 * "Login Streak" (first login per calendar day, consecutive-day tiers) in
 * FE-2647.
 * Modelled as a union + option list so the task-type→fields switch can keep
 * growing without changing call sites.
 */
export type MonthlyTaskType =
  | "FOLLOW_FOR_FOLLOW"
  | "REBETS"
  | "REFERRAL_BONUS"
  | "SPORTSBOOK_PICKS"
  | "CASINO_PICKS"
  | "LOGIN_STREAK";

export type LabeledOption<Value extends string> = {
  value: Value;
  label: string;
  disabled?: boolean;
};

export const MONTHLY_TASK_TYPE_OPTIONS: ReadonlyArray<{
  value: MonthlyTaskType;
  labelKey:
    | "MONTHLY_CHALLENGE_TASK_TYPE_FOLLOW_FOR_FOLLOW"
    | "MONTHLY_CHALLENGE_TASK_TYPE_REBETS"
    | "MONTHLY_CHALLENGE_TASK_TYPE_REFERRAL_BONUS"
    | "MONTHLY_CHALLENGE_TASK_TYPE_SPORTSBOOK_PICKS"
    | "MONTHLY_CHALLENGE_TASK_TYPE_CASINO_PICKS"
    | "GAME_PASS_MONTHLY_TASK_TYPE_LOGIN_STREAK";
}> = [
  {
    value: "FOLLOW_FOR_FOLLOW",
    labelKey: "MONTHLY_CHALLENGE_TASK_TYPE_FOLLOW_FOR_FOLLOW",
  },
  {
    value: "REBETS",
    labelKey: "MONTHLY_CHALLENGE_TASK_TYPE_REBETS",
  },
  {
    value: "REFERRAL_BONUS",
    labelKey: "MONTHLY_CHALLENGE_TASK_TYPE_REFERRAL_BONUS",
  },
  {
    value: "SPORTSBOOK_PICKS",
    labelKey: "MONTHLY_CHALLENGE_TASK_TYPE_SPORTSBOOK_PICKS",
  },
  {
    value: "CASINO_PICKS",
    labelKey: "MONTHLY_CHALLENGE_TASK_TYPE_CASINO_PICKS",
  },
  {
    value: "LOGIN_STREAK",
    labelKey: "GAME_PASS_MONTHLY_TASK_TYPE_LOGIN_STREAK",
  },
];

export const DEFAULT_MONTHLY_TASK_TYPE: MonthlyTaskType = "FOLLOW_FOR_FOLLOW";

/**
 * Play / Win action for the "Casino Picks" and "Sportsbook Picks" task types —
 * whether a player must play/place a qualifying game or bet, or win on it, for
 * their activity to count toward the challenge. Kept local (own option list +
 * label keys) so the monthly module owns its vocabulary.
 */
export type MonthlyPlayWinAction = "PLAY" | "WIN";

export const MONTHLY_ACTION_OPTIONS: ReadonlyArray<{
  value: MonthlyPlayWinAction;
  labelKey: "MONTHLY_CHALLENGE_ACTION_PLAY" | "MONTHLY_CHALLENGE_ACTION_WIN";
}> = [
  { value: "PLAY", labelKey: "MONTHLY_CHALLENGE_ACTION_PLAY" },
  { value: "WIN", labelKey: "MONTHLY_CHALLENGE_ACTION_WIN" },
];

/** Action defaults to "Play" (the widest net) when a Play/Win task type is
 *  first selected — matches the Figma's pre-selected pill. */
export const DEFAULT_MONTHLY_ACTION: MonthlyPlayWinAction = "PLAY";

/**
 * Casino-scoped aliases retained so the casino fields + their tests keep their
 * original imports; both task types share the same Play / Win vocabulary, so
 * new code should prefer the generic {@link MonthlyPlayWinAction} /
 * {@link MONTHLY_ACTION_OPTIONS} / {@link DEFAULT_MONTHLY_ACTION}.
 */
export type MonthlyCasinoAction = MonthlyPlayWinAction;
export const MONTHLY_CASINO_ACTION_OPTIONS = MONTHLY_ACTION_OPTIONS;
export const DEFAULT_MONTHLY_CASINO_ACTION = DEFAULT_MONTHLY_ACTION;

/**
 * Pick Type for the "Sportsbook Picks" task type — which bets qualify toward the
 * challenge. Distinct from Season's `BetType` (no SGP here): the monthly variant
 * offers only Any / Single / Parlay / Live per FE-2255.
 */
export type MonthlyPickType = "ANY" | "SINGLE" | "PARLAY" | "LIVE";

export const MONTHLY_PICK_TYPE_OPTIONS: ReadonlyArray<{
  value: MonthlyPickType;
  labelKey:
    | "MONTHLY_CHALLENGE_PICK_TYPE_ANY"
    | "MONTHLY_CHALLENGE_PICK_TYPE_SINGLE"
    | "MONTHLY_CHALLENGE_PICK_TYPE_PARLAY"
    | "MONTHLY_CHALLENGE_PICK_TYPE_LIVE";
}> = [
  { value: "ANY", labelKey: "MONTHLY_CHALLENGE_PICK_TYPE_ANY" },
  { value: "SINGLE", labelKey: "MONTHLY_CHALLENGE_PICK_TYPE_SINGLE" },
  { value: "PARLAY", labelKey: "MONTHLY_CHALLENGE_PICK_TYPE_PARLAY" },
  { value: "LIVE", labelKey: "MONTHLY_CHALLENGE_PICK_TYPE_LIVE" },
];

/** Pick Type defaults to "Any" (the widest net) when Sportsbook Picks is first
 *  selected — matches the Figma's pre-selected value. */
export const DEFAULT_MONTHLY_PICK_TYPE: MonthlyPickType = "ANY";

/**
 * Task types that collect the sportsbook pick trigger fields (Pick Type,
 * Minimum Odds, Action, Sports / Leagues, Minimum Bet Amount, Wager Currency).
 * Rebets joined Sportsbook Picks in FE-2441 — a re-bet is still a sportsbook
 * pick, so it qualifies on the same triggers. Shared by the card's field
 * switch, the save validation, and the task mapper so the three can't drift.
 */
export const usesSportsbookPickFields = (
  taskType?: MonthlyTaskType,
): boolean => taskType === "SPORTSBOOK_PICKS" || taskType === "REBETS";

/**
 * Keep digits plus an optional single leading sign — used by the Minimum Odds
 * field, which takes American odds (e.g. `-200` / `+150`), always integers.
 * Mirrors `sanitizeSignedDecimalInput` from `createSeasonData` but drops the
 * decimal point since odds have no fractional part.
 */
export const sanitizeSignedIntegerInput = (value: string): string => {
  const sign = value.startsWith("+") || value.startsWith("-") ? value[0] : "";
  return sign + sanitizeIntegerInput(sign ? value.slice(1) : value);
};

/** Reward unit a tier defaults to when a challenge is first created. Uses a
 *  play-currency reward (Rebet Coins) so the pre-selection is always one of the
 *  config-driven reward options the monthly wizard offers (Rebet Coin / Rebet
 *  Cash) — never XP, which is no longer surfaced. On read, a tier's reward type
 *  is derived from its persisted `currency`, so this only seeds brand-new /
 *  currency-less tiers. */
export const DEFAULT_TIER_REWARD_TYPE: RewardType = "REBET_COINS";

/**
 * One tier of a monthly challenge. For "Follow for follow", `threshold` is the
 * follower count the player must reach; `rewardAmount` + `rewardType` is what
 * they earn for reaching it. Amounts are kept as strings so partial / empty
 * inputs stay valid mid-edit.
 *
 * `xpReward` is a required per-tier XP bonus granted alongside the currency
 * reward — it maps to the backend's standalone `xp_reward` tier field
 * (`{ threshold, rewards: [...], xp_reward }`), distinct from the currency
 * reward. Like `threshold` / `rewardAmount` it's kept as a string so a blank /
 * partial input stays valid mid-edit, but a tier isn't valid (can't be saved)
 * until it holds a positive integer.
 */
export type MonthlyTierDraft = {
  id: string;
  threshold: string;
  rewardAmount: string;
  /** Select value: a currency `RewardType` (`REBET_COINS` / `REBET_CASH`) or a
   *  bonus category from `config.bonus_reward_types` (`free_pick`,
   *  `profit_boost`, `deposit_match`). */
  rewardType?: string;
  /** Bonus rewards only — the BE grant identifier (e.g. `free_cash_pick`). */
  bonusRewardType?: string;
  /** Bonus rewards only — the BE `currency` shipped on the wire (e.g.
   *  `free_pick`). */
  bonusCurrency?: string;
  /** Bonus rewards only — editor values for `config.bonus_reward_types[*].params`. */
  params?: Record<string, string>;
  xpReward: string;
};

/** A Profit Boost can never apply to a single-leg pick — it always needs at
 *  least two. The backend advertises `min_legs` as an optional param defaulting
 *  to 2, so nothing stopped an admin from saving 1 and shipping a boost that
 *  never pays out (FE-2691). The monthly editor keeps 2 as a floor and still
 *  lets the admin raise it (3, 4, …): a below-floor value stays on the input
 *  with an inline error so the admin can fix it, rather than being rewritten.
 */
export const MIN_LEGS_PARAM = "min_legs";
export const MIN_LEGS_FLOOR = 2;
export const MIN_LEGS_FLOOR_VALUE = String(MIN_LEGS_FLOOR);

/** True when Min legs holds a complete number below the Profit Boost floor.
 *  Empty is "not yet filled" (the Save gate / mapper default handle that), not
 *  a below-floor error — same split as Season's Number of Legs hint.
 */
export const isMinLegsBelowFloor = (raw: string | undefined): boolean => {
  const trimmed = (raw ?? "").trim();
  if (trimmed === "") return false;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed < MIN_LEGS_FLOOR;
};

/** Raise a drafted Min legs value onto the Profit Boost floor. Empty / NaN /
 *  values below 2 become `"2"`; anything already at or above the floor is kept
 *  so an admin-authored 3+ survives save. Used on the wire only — the editor
 *  does not rewrite the typed value. */
export const clampMinLegsValue = (raw: string | undefined): string => {
  const parsed = Number.parseInt(raw ?? "", 10);
  if (!Number.isFinite(parsed) || parsed < MIN_LEGS_FLOOR) {
    return MIN_LEGS_FLOOR_VALUE;
  }
  return String(parsed);
};

/**
 * Floor `min_legs` on the way to the wire, so a campaign persisted with 1
 * before this gate existed can't round-trip that value back — without wiping
 * an admin-chosen 3+. Only rewrites a key the reward already carries — a bonus
 * type without `min_legs` (Deposit Match, Free Pick) must not gain one.
 */
export const clampMinLegsParam = (
  params: MonthlyTierDraft["params"],
): MonthlyTierDraft["params"] =>
  params && MIN_LEGS_PARAM in params
    ? { ...params, [MIN_LEGS_PARAM]: clampMinLegsValue(params[MIN_LEGS_PARAM]) }
    : params;

export type MonthlyChallengeDraft = {
  id: string;
  name: string;
  description: string;
  taskType: MonthlyTaskType;
  /** "Sportsbook Picks" / "Rebets" (FE-2441) — which bets qualify toward the challenge.
   *  Defaults to "ANY"; ignored (but preserved) for other task types so
   *  switching task type back and forth doesn't lose the selection. */
  pickType?: MonthlyPickType;
  /** "Sportsbook Picks" / "Rebets" (FE-2441) — American-odds floor a pick must meet to count
   *  (e.g. "-200" / "+150"). Kept as a free-form string so partial / signed
   *  inputs stay valid mid-edit. */
  minimumOdds?: string;
  /** "Sportsbook Picks" / "Rebets" (FE-2441) — whether a player must place (play) or win a
   *  qualifying pick for it to count. Defaults to "PLAY". On a Rebets task the
   *  two pills carry the FE-2535 wire semantics: "Play" ships the
   *  `count_on_placement` rule (the pick owner is credited the moment a re-bet
   *  of their pick is placed), "Win" ships `outcome_required: WIN` (the re-bet
   *  must settle as a winner) — `ANY_SETTLED` is not valid on a
   *  `rebets_received` task. */
  sportsbookAction?: MonthlyPlayWinAction;
  /** "Sportsbook Picks" / "Rebets" (FE-2441) — selected sport ids that qualify (empty = any
   *  sport). Catalogue from `getSportOptions`. Ignored (but preserved) for
   *  other task types. */
  sports?: ReadonlyArray<string>;
  /** "Sportsbook Picks" / "Rebets" (FE-2441) — selected league ids under {@link sports}
   *  (empty = any league of the chosen sports). Pruned when a parent sport is
   *  deselected so a league can never dangle without its sport. */
  leagues?: ReadonlyArray<string>;
  /** "Sportsbook Picks" / "Rebets" (FE-2441) — the minimum wager (in the primary currency) a
   *  pick must meet to count. Required for Sportsbook Picks (positive number);
   *  free-form string so partial inputs stay valid mid-edit. */
  sportsbookMinBet?: string;
  /** "Casino Picks" only — the provider names whose games qualify (e.g.
   *  ["evolution", "pragmatic"]). Multiple providers may be selected. Ignored
   *  (but preserved) for other task types. */
  casinoProviders?: ReadonlyArray<string>;
  /** "Casino Picks" only — individually-selected qualifying game ids, chosen
   *  across any provider. Independent of {@link casinoProviders}; empty means
   *  "any game from the selected providers". */
  casinoGames?: ReadonlyArray<string>;
  /** "Casino Picks" only — display names for {@link casinoGames}, keyed by game
   *  id, so the multi-select shows readable tags after the panel closes. */
  casinoGameLabels?: Readonly<Record<string, string>>;
  /** "Casino Picks" only — whether a player must play or win to make progress.
   *  Defaults to "PLAY". */
  casinoAction?: MonthlyCasinoAction;
  /** "Casino Picks" only — the minimum wager (in the primary currency) a play
   *  must meet to count toward the challenge. Required for Casino Picks (must be
   *  a positive number); kept as a free-form string so partial inputs stay valid
   *  mid-edit. Ignored (but preserved) for other task types. */
  casinoMinBet?: string;
  /** "Sportsbook Picks" / "Rebets" / "Casino Picks" — the currency a qualifying
   *  wager must be placed in. Sourced from `GET /config` currencies (same as
   *  the Season task form), so the value is a UI {@link BetCurrency}. Required
   *  for both pick task types. Ignored (but preserved) for other task types. */
  wagerCurrency?: BetCurrency;
  /** Login Streak (FE-2668) — UTC hour (0–23) the daily window resets. Kept as
   *  a string so the Select can stay empty mid-edit. Ignored (but preserved)
   *  for other task types. Maps to campaign `streak_reset_hour`. */
  streakResetHour?: string;
  tiers: ReadonlyArray<MonthlyTierDraft>;
  /** UI-only: `true` while the inline editor is open; `false` renders the
   *  read-only collapsed summary. Not persisted. */
  isExpanded: boolean;
  /** UI-only: flips to `true` on the first explicit "Save Challenge" click. A
   *  saved challenge collapses to its summary and gains a Delete affordance when
   *  re-opened; an unsaved one is discarded when closed. Not persisted. */
  isSaved: boolean;
  /** Backend challenge-group id (`challenge_id`), present only for cards
   *  re-hydrated from the API on the Monthly Details page. Its presence marks
   *  the card as already persisted, so "Save Challenge" issues a PUT rather
   *  than a create. `undefined` for cards authored in-session (wizard / Add
   *  Challenge). Never sent in a request body. */
  serverChallengeId?: string;
  /** Backend tiered-task id (`task_id`) for the card's single task, paired with
   *  {@link serverChallengeId}. Needed to target `PUT /admin/tasks/{task_id}`.
   *  `undefined` for in-session cards. Never sent in a request body. */
  serverTaskId?: string;
};

export type MonthlyChallengesValues = {
  challenges: ReadonlyArray<MonthlyChallengeDraft>;
};

export const MONTHLY_CHALLENGES_INITIAL_VALUES: MonthlyChallengesValues = {
  challenges: [],
};

/** At least one tier per challenge — the last tier can't be removed. */
export const MIN_TIERS_PER_CHALLENGE = 1;

/**
 * Build a stable id with a counter suffix so two adds in the same millisecond
 * (jsdom fires synchronous user-event clicks within one tick) still produce
 * unique React / list keys.
 */
let idCounter = 0;
const buildLocalId = (prefix: string): string => {
  idCounter += 1;
  return `${prefix}-${Date.now()}-${idCounter}`;
};

export const createEmptyTier = (): MonthlyTierDraft => ({
  id: buildLocalId("tier"),
  threshold: "",
  rewardAmount: "",
  rewardType: DEFAULT_TIER_REWARD_TYPE,
  xpReward: "",
});

export const createEmptyMonthlyChallenge = (): MonthlyChallengeDraft => ({
  id: buildLocalId("monthly-challenge"),
  name: "",
  description: "",
  taskType: DEFAULT_MONTHLY_TASK_TYPE,
  pickType: DEFAULT_MONTHLY_PICK_TYPE,
  minimumOdds: "",
  sportsbookAction: DEFAULT_MONTHLY_ACTION,
  sports: [],
  leagues: [],
  sportsbookMinBet: "",
  casinoProviders: [],
  casinoGames: [],
  casinoGameLabels: {},
  casinoAction: DEFAULT_MONTHLY_CASINO_ACTION,
  casinoMinBet: "",
  wagerCurrency: undefined,
  streakResetHour: "",
  tiers: [createEmptyTier()],
  // A brand-new challenge opens straight into the editor, unsaved.
  isExpanded: true,
  isSaved: false,
});

/**
 * Clone a challenge for the Duplicate action: a brand-new challenge (fresh ids
 * for the challenge and every tier so keys never alias the source) with a
 * "(copy)" name suffix and all field values preserved.
 */
export const duplicateMonthlyChallenge = (
  challenge: MonthlyChallengeDraft,
  copySuffix: string,
): MonthlyChallengeDraft => ({
  ...challenge,
  id: buildLocalId("monthly-challenge"),
  name: `${challenge.name}${copySuffix}`,
  tiers: challenge.tiers.map((tier) => ({
    ...tier,
    id: buildLocalId("tier"),
  })),
  // A duplicate is a brand-new, not-yet-persisted card — drop the source's
  // backend ids so it never targets the original's challenge / task on save.
  serverChallengeId: undefined,
  serverTaskId: undefined,
  // The copy opens as a fresh editable draft so the user can tweak it before
  // committing, regardless of the source challenge's saved state.
  isExpanded: true,
  isSaved: false,
});

const isPositiveInteger = (value: string): boolean => {
  if (value.trim() === "") return false;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0;
};

/** A required money value (e.g. the casino Minimum Bet Amount): non-empty and
 *  parsing to a finite, strictly positive number. Fractional amounts like
 *  "10.5" are allowed since a bet minimum can be a decimal. */
export const isPositiveAmount = (value: string): boolean => {
  if (value.trim() === "") return false;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

export const isTierValid = (tier: Partial<MonthlyTierDraft>): boolean =>
  isPositiveInteger(tier?.threshold ?? "") &&
  isPositiveInteger(tier?.rewardAmount ?? "") &&
  isPositiveInteger(tier?.xpReward ?? "") &&
  Boolean(tier?.rewardType);

/** American odds are a signed integer (e.g. "-200" / "+150" / "110"). A blank
 *  or sign-only string ("+") is not a valid value — the sanitizer keeps a lone
 *  sign so the user can type it before the digits, so validity is enforced
 *  here rather than in the input. */
export const isValidAmericanOdds = (value: string): boolean =>
  /^[+-]?\d+$/.test(value);

/** UTC hour 0–23 as accepted by campaign `streak_reset_hour`. Blank / out of
 *  range / non-integer values are not a valid hour. */
export const parseUtcHour = (
  value?: string | number | null,
): number | undefined => {
  if (typeof value === "number") {
    return Number.isInteger(value) && value >= 0 && value <= 23
      ? value
      : undefined;
  }
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!/^(?:[01]?\d|2[0-3])$/.test(trimmed)) return undefined;
  return Number(trimmed);
};

export const isValidUtcHour = (value?: string): boolean =>
  parseUtcHour(value) !== undefined;

/** Select values for the Login Streak daily reset hour. Labels are applied at
 *  the call site via AppStrings so TCM can recopy them. */
export const UTC_STREAK_RESET_HOURS: ReadonlyArray<number> = Array.from(
  { length: 24 },
  (_, hour) => hour,
);

/** First Login Streak card's authored UTC hour, or `undefined` when none. */
export const pickStreakResetHour = (
  challenges: ReadonlyArray<Partial<MonthlyChallengeDraft>>,
): number | undefined => {
  for (const challenge of challenges) {
    if (challenge.taskType !== "LOGIN_STREAK") continue;
    const hour = parseUtcHour(challenge.streakResetHour);
    if (hour !== undefined) return hour;
  }
  return undefined;
};

export const isMonthlyChallengeValid = (
  challenge: Partial<MonthlyChallengeDraft>,
): boolean => {
  if (typeof challenge?.name !== "string" || challenge.name.trim() === "") {
    return false;
  }
  if (
    typeof challenge?.description !== "string" ||
    challenge.description.trim() === ""
  ) {
    return false;
  }
  // Sportsbook Picks and Rebets (same trigger fields since FE-2441) gate on a
  // valid Minimum Odds value, a valid Minimum Bet Amount and a chosen Wager
  // Currency; the Pick Type and Action always have defaults and the Sports /
  // Leagues are optional ("any sport / league"), so none of those block.
  if (
    usesSportsbookPickFields(challenge.taskType) &&
    (!isValidAmericanOdds(challenge.minimumOdds ?? "") ||
      !isPositiveAmount(challenge.sportsbookMinBet ?? "") ||
      !challenge.wagerCurrency)
  ) {
    return false;
  }
  // Casino Picks requires at least one provider or game to be chosen, a valid
  // Minimum Bet Amount and a chosen Wager Currency; the Action always has a
  // default. Providers and games are independent selections (mirrors the Season
  // casino picker).
  if (
    challenge.taskType === "CASINO_PICKS" &&
    (((challenge.casinoProviders ?? []).length === 0 &&
      (challenge.casinoGames ?? []).length === 0) ||
      !isPositiveAmount(challenge.casinoMinBet ?? "") ||
      !challenge.wagerCurrency)
  ) {
    return false;
  }
  // Login Streak requires a UTC reset hour (0–23) so the campaign can stamp
  // `streak_reset_hour` — omitting it would leave the window unconfigured.
  if (
    challenge.taskType === "LOGIN_STREAK" &&
    !isValidUtcHour(challenge.streakResetHour)
  ) {
    return false;
  }
  const tiers = challenge?.tiers;
  return (
    Array.isArray(tiers) &&
    tiers.length >= MIN_TIERS_PER_CHALLENGE &&
    tiers.every(isTierValid)
  );
};

/** Wizard "Next" gate for the Challenges step — mirrors the Season step, which
 *  only requires at least one challenge to exist (per-challenge validity is
 *  surfaced inline, not blocking here). */
export const hasAtLeastOneChallenge = (
  values: Partial<MonthlyChallengesValues>,
): boolean =>
  Array.isArray(values?.challenges) && values.challenges.length > 0;

/**
 * Move the challenge with id `fromId` into the slot occupied by `toId`,
 * returning the reordered list. Mirrors Season's `reorderChallenges`: the
 * order is local-only here (no backend `sort_order` yet), so this is the whole
 * reorder — the drag handler just swaps this into state. Returns the original
 * reference on a no-op (self-drop or unknown id) so React can skip a re-render.
 */
export const reorderMonthlyChallenges = (
  challenges: ReadonlyArray<MonthlyChallengeDraft>,
  fromId: string,
  toId: string,
): ReadonlyArray<MonthlyChallengeDraft> => {
  if (fromId === toId) return challenges;
  const fromIndex = challenges.findIndex((c) => c.id === fromId);
  const toIndex = challenges.findIndex((c) => c.id === toId);
  if (fromIndex === -1 || toIndex === -1) return challenges;
  const next = [...challenges];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};
